import React from 'react';
import { Button } from '../../Components/MUIs';
import Canvas from '../../Components/Canvas/Canvas';

export default function M_EXT_SIGN_BASIC({
  inputs,
  consentChecked,
  setConsentChecked,
  signatureCanvas,
  signInputs,
  setSignInputs,
  signRegistered = false,
  isEditingSign = false,
  onClear,
  onConfirm,
}) {
  const signLocked = signRegistered && !isEditingSign;

  return (
    <div className="flex min-h-[100dvh] flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <h1 className="text-lg font-bold text-slate-900">외부 참여자 서명</h1>
        <p className="mt-0.5 text-xs text-slate-500">
          세이프온 회원이 아니어도 안내 링크로 접속해 서명할 수 있습니다.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-800">회의 제목</p>
          <p className="mt-1 text-base font-medium text-slate-900">
            {inputs.proceedingsTitle || '회의 제목 없음'}
          </p>
          <p className="mt-1 text-xs break-all text-slate-500">
            회의 일시:{' '}
            {inputs.proceedingsDate
              ? inputs.proceedingsDate.substring(0, 10)
              : '-'}
          </p>
        </div>

        <div className="mt-4 space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              성명 <span className="text-rose-600">*</span>
            </label>
            <input
              className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 ring-0 outline-none"
              placeholder="실명을 입력해 주세요"
              value={inputs.name}
              readOnly
              disabled
              maxLength={50}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              소속(선택)
            </label>
            <input
              className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 ring-0 outline-none"
              placeholder="회사 또는 조직명"
              value={inputs.depNm}
              readOnly
              disabled
              maxLength={100}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-700">
              연락처(선택)
            </label>
            <input
              className="h-11 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 ring-0 outline-none"
              placeholder="01012345678"
              value={inputs.tel}
              readOnly
              disabled
              maxLength={11}
              inputMode="numeric"
            />
          </div>
        </div>

        {signRegistered && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900">
              서명이 완료된 회의입니다
            </p>
            <p className="mt-1 text-xs leading-relaxed text-amber-800">
              {isEditingSign
                ? '새 서명을 입력한 뒤 서명 제출을 눌러 주세요.'
                : '등록된 서명을 확인할 수 있습니다. 변경하려면 서명 지우기를 눌러 주세요.'}
            </p>
          </div>
        )}

        <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold text-slate-800">서명</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            {signLocked
              ? '등록된 서명입니다. 변경하려면 서명 지우기를 눌러 주세요.'
              : '아래 영역에 손가락 또는 스타일러스로 서명해 주세요. 제출 시 전자서명으로 간주됩니다.'}
          </p>
          <div className="mt-3">
            <Canvas
              signatureCanvas={signatureCanvas}
              userId={inputs.name}
              imageUrl={signInputs.mapUrl}
              disabled={signLocked}
              onEnd={() => {
                if (signLocked || !signatureCanvas.current) return;
                const signatureDataUrl = signatureCanvas.current.toDataURL();
                const dataURL = signatureCanvas.current.toDataURL('image/png');
                const decodedData = atob(dataURL.split(',')[1]);
                const byteNumbers = new Array(decodedData.length);

                for (let i = 0; i < decodedData.length; i++) {
                  byteNumbers[i] = decodedData.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                  type: 'image/png',
                });
                const file = new File(
                  [blob],
                  `${new Date()
                    .toISOString()
                    .replace(/T/, '')
                    .replace(/\..+/, '')
                    .replace(/-/g, '')
                    .replace(/:/g, '')}.png`,
                  {
                    type: 'image/png',
                  }
                );

                setSignInputs({
                  mapUrl: signatureDataUrl,
                  mapFiles: file,
                });
              }}
            />
          </div>
          <div className="mt-3">
            <Button
              variant="outlined"
              className="h-9 w-full rounded-full"
              name="서명 지우기"
              onClick={onClear}
            />
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#2CA900] focus:ring-[#2CA900]"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
            />
            <span className="text-xs leading-snug text-slate-700">
              [필수] 본인은 위 내용을 확인하였으며, 서명 정보 수집·이용 및
              전자서명에 동의합니다.
            </span>
          </label>
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <Button
          className="h-11 w-full rounded-full bg-[#2EBD36] text-white disabled:bg-slate-300 disabled:text-slate-500"
          name={signLocked ? '서명 완료' : '서명 제출'}
          onClick={onConfirm}
          disabled={signLocked}
        />
      </div>
    </div>
  );
}
